import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { fundingLevels } from '../utils/mockData';
import { useLanguage } from '../contexts/LanguageContext';

const FundingLevelsSection = () => {
  const { t, language } = useLanguage();

  return (
    <section className="py-24 sm:py-32 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-sm font-semibold text-eucalyptus-600 tracking-wider uppercase mb-4"
          >
            {t('Support at Home Program', '居家养老计划')}
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-900 mb-6 tracking-tight leading-tight"
          >
            {t('Eight funding levels,', '八个资助等级，')}
            <br />
            <span className="text-eucalyptus-600">
              {t('one right for you', '总有一款适合您')}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-600 leading-relaxed"
          >
            {t(
              'Australian Government-funded support tailored to your care needs — from occasional check-ins to comprehensive 24-hour care.',
              '澳大利亚政府资助的定制化护理服务 — 从偶尔的健康检查到全面的24小时护理。'
            )}
          </motion.p>
        </div>

        {/* Levels Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {fundingLevels.map((level, index) => {
            const isHighlighted = level.level === 4;
            return (
              <motion.div
                key={level.level}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className={`relative rounded-3xl p-6 transition-all duration-300 hover:-translate-y-1 ${
                  isHighlighted
                    ? 'bg-navy-900 text-white shadow-xl'
                    : 'bg-white text-navy-900 hover:shadow-xl border border-gray-100'
                }`}
                data-testid={`funding-level-${level.level}`}
              >
                {isHighlighted && (
                  <div className="absolute -top-3 left-6 px-3 py-1 bg-eucalyptus-500 text-white text-xs font-bold rounded-full">
                    {t('MOST COMMON', '最常见')}
                  </div>
                )}
                
                {/* Level badge */}
                <div className={`inline-flex items-center gap-2 mb-4 ${
                  isHighlighted ? 'text-eucalyptus-300' : 'text-eucalyptus-600'
                }`}>
                  <span className="text-xs font-bold tracking-widest">{level.code}</span>
                  <span className={`text-xs ${isHighlighted ? 'text-white/50' : 'text-gray-400'}`}>
                    · {t('Level', '等级')} {level.level}
                  </span>
                </div>

                {/* Amount */}
                <div className="mb-4">
                  <div className={`text-3xl font-bold mb-1 ${
                    isHighlighted ? 'text-white' : 'text-navy-900'
                  }`}>
                    {level.amount}
                  </div>
                  <div className={`text-xs ${
                    isHighlighted ? 'text-white/60' : 'text-gray-500'
                  }`}>
                    {language === 'zh' && level.periodZh ? level.periodZh : level.period}
                  </div>
                </div>

                {/* Name */}
                <h3 className={`text-base font-bold mb-3 leading-tight ${
                  isHighlighted ? 'text-white' : 'text-navy-900'
                }`}>
                  {language === 'zh' && level.nameZh ? level.nameZh : level.name}
                </h3>

                {/* Description */}
                <p className={`text-xs leading-relaxed ${
                  isHighlighted ? 'text-white/80' : 'text-gray-600'
                }`}>
                  {language === 'zh' && level.descriptionZh ? level.descriptionZh : level.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Bottom info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          {[
            { title: t('Assessed by ACAT', '由ACAT评估'), desc: t('Free government assessment determines your level', '免费政府评估确定您的等级') },
            { title: t('Fully subsidised', '全额补贴'), desc: t('Australian Government pays for your approved services', '澳大利亚政府支付您批准的服务') },
            { title: t('Flexible & choice', '灵活自主'), desc: t('You decide what services you receive and when', '您决定接受哪些服务及何时接受') }
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-eucalyptus-100 flex items-center justify-center mt-0.5">
                <Check className="w-4 h-4 text-eucalyptus-700" />
              </div>
              <div>
                <div className="font-semibold text-navy-900 mb-1">{item.title}</div>
                <div className="text-sm text-gray-600">{item.desc}</div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 text-center"
        >
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-navy-900 hover:bg-navy-800 text-white rounded-full font-semibold transition-all shadow-lg hover:shadow-xl"
            data-testid="funding-cta"
          >
            {t('Talk to us about your options', '咨询您的服务选项')}
            <ArrowRight className="w-5 h-5" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FundingLevelsSection;
